export type EntityRef = {
  id: string;
  label?: string;
  /**
   * `selection` — an entity the request asked for, and the only kind a response
   * echoes back. `modifier` — a filter that scopes the query (gender, msa,
   * provider…) and is never returned as an entity, so it must stay out of the
   * returned-vs-requested comparison.
   */
  kind: 'selection' | 'modifier';
  /** `gender`, `role`, `company`… — derived from where it sat in the payload. */
  category: string;
  /** Full location, e.g. `composition_v3.entities[0].metrics.gender[0]`. */
  path: string;
};

/**
 * Keys whose value IS the entity id on the object carrying them. `EntID` is the
 * request-side spelling (`CompositionV3Ent`); the rest are response-side.
 */
const ID_KEYS = [
  'id',
  'EntID',
  'entity_id',
  'entityId',
  'rcid',
  'RCID',
  'user_id',
  'userId',
  'company_id',
  'companyId',
  'school_id',
  'schoolId',
  'msa',
  'fips',
  'drilldownId',
];

/**
 * Consulted only when no {@link ID_KEYS} entry yielded an id on the same object.
 *
 * `index` means two different things depending on where it sits. On `pay_v2`'s
 * `top_companies`/`top_roles`/`top_geos` it IS the entity id — those objects
 * carry no `id`, and `TopNDataProvider` feeds `index` straight back into the
 * next request as `EntID`. (`pay_v3` renames that id to `id` and drops `index`
 * entirely, so v3 pay responses resolve through {@link ID_KEYS} and never hit
 * this fallback.) On a composition/postings/sentiment/transitions entity it is
 * the request array position, used to zip a response row back to the entity
 * that asked for it, and a real `id` sits alongside it.
 *
 * Promoting `index` to {@link ID_KEYS} would therefore add a junk positional
 * chip to every row of those four responses. Falling back only when nothing
 * else produced an id keeps it to the shapes that have no other identifier —
 * and, because the test is "no id was produced" rather than "no id key is
 * present", it also covers an explicit `id: null`.
 */
const FALLBACK_ID_KEYS = ['index'];

/** A labelled object is an entity; an unlabelled one is a datapoint. */
const LABEL_KEYS = [
  'short_name',
  'shortName',
  'long_name',
  'longName',
  'name',
  'label',
  'dim',
  'title',
];

/**
 * Request-side containers of bare ids — `filters: { company: [1116644] }` and
 * `entities: [{ EntID }]`. Under these the KEY names the category, so a value
 * needs no label of its own to be meaningful.
 */
const ID_LIST_PARENTS = ['filters', 'entities', 'ids', 'entity_ids', 'rcids'];

/** Guard rails for a payload that is large, deep, or self-referential. */
const MAX_DEPTH = 12;
/** Exported so the drawer can say "capped" rather than imply an exact count. */
export const MAX_REFS = 500;

/** Path segments that describe structure rather than a kind of entity. */
const STRUCTURAL_SEGMENTS = new Set([
  'metrics',
  'data',
  'req',
  'filters',
  'breakdowns',
  'breakdown',
  'headcount',
]);

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const asId = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value.length > 0) return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return undefined;
};

const findLabel = (object: Record<string, unknown>): string | undefined => {
  for (const key of LABEL_KEYS) {
    const label = object[key];
    if (typeof label === 'string' && label.length > 0) return label;
  }
  return undefined;
};

const joinPath = (path: string, segment: string) =>
  path.length === 0 ? segment : `${path}.${segment}`;

/** Array indices are noise when matching a path against a container name. */
const stripIndices = (path: string) => path.replace(/\[\d+\]/g, '');

const isIdListPath = (path: string) => {
  const normalised = stripIndices(path);
  return ID_LIST_PARENTS.some(
    (parent) => normalised === parent || normalised.endsWith(`.${parent}`)
  );
};

/**
 * Anything under `filters` scopes the query rather than naming an entity, so it
 * is never echoed in the response.
 */
const kindForPath = (path: string): 'selection' | 'modifier' => {
  const normalised = stripIndices(path);
  return normalised === 'filters' || normalised.startsWith('filters.')
    ? 'modifier'
    : 'selection';
};

/** `EntType: 'company'` names the category far better than `entities` does. */
const readEntType = (object: Record<string, unknown>): string | undefined => {
  const entType = object['EntType'];
  return typeof entType === 'string' && entType.length > 0
    ? entType
    : undefined;
};

/**
 * `pay_v2` names its top-N blocks after the metric, while a chart card tags
 * itself with the dimension it plots. Aligning the two is what makes the
 * category hover work: the drawer highlights cards by
 * `[data-chart-dimension="<category>"]`, and that attribute carries a
 * `CompensationSecondaryDimension` — `company`, `role`, `geography`. Left as
 * `top_companies` the selector matches nothing at all.
 */
const CATEGORY_ALIASES: Record<string, string> = {
  top_companies: 'company',
  top_roles: 'role',
  top_geos: 'geography',
};

/**
 * The nearest named segment of a path, ignoring array indices and structural
 * wrappers. `composition_v3.entities[0].metrics.gender[0]` → `gender`.
 */
const categoryFromPath = (path: string): string => {
  const segments = path
    .split('.')
    .map((segment) => segment.replace(/\[\d+\]$/, ''))
    .filter((segment) => segment.length > 0);

  for (let index = segments.length - 1; index >= 0; index--) {
    const segment = segments[index];
    if (segment !== undefined && !STRUCTURAL_SEGMENTS.has(segment)) {
      return CATEGORY_ALIASES[segment] ?? segment;
    }
  }
  return 'other';
};

/**
 * Walks any request-variables or response payload and pulls out entity ids,
 * tagged with the category they sat under.
 *
 * Deliberately shape-agnostic: chart data arrives in ten dataset shapes across
 * a dozen providers, and a per-shape extractor would need updating every time
 * one is added.
 *
 * **An object only counts as an entity if it carries a label.** Composition
 * responses nest `{ id, count, share }` timepoints under every breakdown, whose
 * `id` is a month reference — without this rule one response yields hundreds of
 * identical junk ids that drown the real entities. Bare id lists under
 * `filters`/`entities` are the exception: there the key names the category.
 */
const extractEntityRefs = (payload: unknown): EntityRef[] => {
  const refs: EntityRef[] = [];
  const seen = new Set<string>();
  const visited = new WeakSet<object>();

  const add = (
    id: string,
    label: string | undefined,
    path: string,
    categoryOverride?: string
  ) => {
    const category = categoryOverride ?? categoryFromPath(path);
    const dedupeKey = `${category}:${id}:${label ?? ''}`;
    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    refs.push({ id, label, category, path, kind: kindForPath(path) });
  };

  const walk = (value: unknown, path: string, depth: number) => {
    if (refs.length >= MAX_REFS || depth > MAX_DEPTH) return;

    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        walk(item, `${path}[${index}]`, depth + 1);
      });
      return;
    }

    if (!isPlainObject(value)) return;
    if (visited.has(value)) return;
    visited.add(value);

    const label = findLabel(value);

    // A request's `entities: [{ EntID, EntType }]` has no label to carry — the
    // container says these are entities, so the label rule is waived inside it.
    // Without this the whole Requested column reads "none found" on any page
    // whose selection travels in `entities` rather than `filters`.
    const isInsideIdList = isIdListPath(path);

    if (label !== undefined || isInsideIdList) {
      let hasId = false;
      for (const key of ID_KEYS) {
        if (!(key in value)) continue;
        const id = asId(value[key]);
        if (id !== undefined) {
          add(id, label, path, readEntType(value));
          hasId = true;
        }
      }

      if (!hasId) {
        for (const key of FALLBACK_ID_KEYS) {
          if (!(key in value)) continue;
          const id = asId(value[key]);
          if (id !== undefined) add(id, label, path, readEntType(value));
        }
      }
    }

    for (const [key, child] of Object.entries(value)) {
      const childPath = joinPath(path, key);

      // `filters: { company: [1116644] }` — a bare id list whose key is the
      // category, so the values need no label of their own.
      if (isIdListPath(path) && Array.isArray(child)) {
        child.forEach((item, index) => {
          const id = asId(item);
          if (id !== undefined) add(id, undefined, `${childPath}[${index}]`);
        });
      }

      walk(child, childPath, depth + 1);
    }
  };

  walk(payload, '', 0);
  return refs;
};

/**
 * The object a ref was read from, resolved back out of the payload by its path.
 *
 * Held as a path rather than a captured reference: refs are deduplicated, and a
 * captured object would pin the whole response in memory for every chip.
 * Returns `undefined` if the path no longer resolves, which is the honest answer
 * — better a tooltip without a payload than one showing the wrong object.
 */
export const resolvePayloadAtPath = (
  payload: unknown,
  path: string
): unknown => {
  if (path.length === 0) return payload;

  // `composition_v3.entities[0].metrics.role[0]` → the segments between dots,
  // with each trailing `[n]` applied as its own step.
  let current: unknown = payload;

  for (const segment of path.split('.')) {
    const name = segment.replace(/\[\d+\]/g, '');
    if (name.length > 0) {
      if (!isPlainObject(current)) return undefined;
      current = current[name];
    }

    for (const index of segment.matchAll(/\[(\d+)\]/g)) {
      if (!Array.isArray(current)) return undefined;
      current = current[Number(index[1])];
    }
  }

  return current;
};

export type CategoryGroup = { category: string; refs: EntityRef[] };

/** Groups refs for display, biggest category first. */
export const groupRefsByCategory = (refs: EntityRef[]): CategoryGroup[] => {
  const byCategory = new Map<string, EntityRef[]>();
  for (const ref of refs) {
    const existing = byCategory.get(ref.category);
    if (existing) existing.push(ref);
    else byCategory.set(ref.category, [ref]);
  }
  return [...byCategory.entries()]
    .map(([category, categoryRefs]) => ({ category, refs: categoryRefs }))
    .sort((a, b) => b.refs.length - a.refs.length);
};

export type EntityRefDiff = {
  /** Entities the request asked for. */
  requested: EntityRef[];
  /** Filters that scoped the query. Shown for context. */
  modifiers: EntityRef[];
  returned: EntityRef[];
};

/**
 * Splits a request's ids from the response's, for display side by side.
 *
 * It deliberately does NOT report "requested but not returned". Plenty of
 * healthy queries ask for an entity and return a breakdown keyed on something
 * else entirely, so that set was mostly false alarms — it read as a fault
 * report on requests that were working exactly as intended.
 */
export const diffEntityRefs = (
  variables: unknown,
  data: unknown
): EntityRefDiff => {
  const requestRefs = extractEntityRefs(variables);
  const requested = requestRefs.filter((ref) => ref.kind === 'selection');
  const modifiers = requestRefs.filter((ref) => ref.kind === 'modifier');
  const returned = extractEntityRefs(data);

  return { requested, modifiers, returned };
};
